import { useEffect, useMemo, useState } from "react";
import { BaseRepository } from "./base-repository";
import { sql } from "@/domains/database/utils";

export type SmartCardRow = {
  id: string;
  associated_space_account_id: string;
  smart_context_id: string;
  smart_view_id: string;
  sort_key: string | null;
  added_by_space_account_id: string | null;
  derived_kind: string | null;
  derived_internal_label: string | null;
  originated_from_find_more?: boolean | null;
  value_json: unknown;
  refreshed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export class SmartCardRepository extends BaseRepository {
  async upsertCard(params: SmartCardRow): Promise<void> {
    const {
      id,
      associated_space_account_id,
      smart_context_id,
      smart_view_id,
      sort_key = null,
      added_by_space_account_id = null,
      derived_kind = null,
      derived_internal_label = null,
      originated_from_find_more = null,
      value_json,
      refreshed_at = null,
      created_at = new Date().toISOString(),
      updated_at = new Date().toISOString(),
    } = params;

    await this.db.query(
      sql`
        INSERT INTO smart_cards (
          id,
          associated_space_account_id,
          smart_context_id,
          smart_view_id,
          sort_key,
          added_by_space_account_id,
          derived_kind,
          derived_internal_label,
          originated_from_find_more,
          value_json,
          refreshed_at,
          created_at,
          updated_at
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13)
        ON CONFLICT (id) DO UPDATE SET
          associated_space_account_id = EXCLUDED.associated_space_account_id,
          smart_context_id = EXCLUDED.smart_context_id,
          smart_view_id = EXCLUDED.smart_view_id,
          sort_key = EXCLUDED.sort_key,
          added_by_space_account_id = EXCLUDED.added_by_space_account_id,
          derived_kind = EXCLUDED.derived_kind,
          derived_internal_label = EXCLUDED.derived_internal_label,
          originated_from_find_more = EXCLUDED.originated_from_find_more,
          value_json = EXCLUDED.value_json,
          refreshed_at = EXCLUDED.refreshed_at,
          updated_at = EXCLUDED.updated_at
      `,
      [
        id,
        associated_space_account_id,
        smart_context_id,
        smart_view_id,
        sort_key,
        added_by_space_account_id,
        derived_kind,
        derived_internal_label,
        originated_from_find_more,
        JSON.stringify(value_json),
        refreshed_at,
        created_at,
        updated_at,
      ]
    );
  }

  async replaceCardsForSmartView(params: {
    smartViewId: string;
    rows: Array<
      Pick<
        SmartCardRow,
        | "id"
        | "associated_space_account_id"
        | "smart_context_id"
        | "smart_view_id"
        | "sort_key"
        | "added_by_space_account_id"
        | "derived_kind"
        | "derived_internal_label"
        | "originated_from_find_more"
        | "value_json"
        | "refreshed_at"
        | "created_at"
        | "updated_at"
      >
    >;
  }): Promise<void> {
    const { smartViewId, rows } = params;

    // Perform an atomic replacement to avoid flicker in live queries
    try {
      await this.db.query(sql`BEGIN`);

      await this.db.query(sql`DELETE FROM smart_cards WHERE smart_view_id = $1`, [smartViewId]);

      for (const row of rows) {
        await this.db.query(
          sql`
            INSERT INTO smart_cards (
              id,
              associated_space_account_id,
              smart_context_id,
              smart_view_id,
              sort_key,
              added_by_space_account_id,
              derived_kind,
              derived_internal_label,
              originated_from_find_more,
              value_json,
              refreshed_at,
              created_at,
              updated_at
          ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13)
          `,
          [
            row.id,
            row.associated_space_account_id,
            row.smart_context_id,
            row.smart_view_id,
            row.sort_key ?? null,
            row.added_by_space_account_id ?? null,
            row.derived_kind ?? null,
            row.derived_internal_label ?? null,
            row.originated_from_find_more ?? null,
            JSON.stringify(row.value_json),
            row.refreshed_at ?? null,
            row.created_at ?? new Date().toISOString(),
            row.updated_at ?? new Date().toISOString(),
          ]
        );
      }

      await this.db.query(sql`COMMIT`);
    } catch (error) {
      await this.db.query(sql`ROLLBACK`);
      throw error;
    }
  }

  async deleteById({ cardId }: { cardId: string }): Promise<void> {
    await this.db.query(sql`DELETE FROM smart_cards WHERE id = $1`, [cardId]);
  }

  useCardsBySmartViewId({ smartViewId }: { smartViewId: string | null }): {
    cards: SmartCardRow[];
    isLoading: boolean;
    error: string | null;
  } {
    const [cards, setCards] = useState<SmartCardRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const query = useMemo(
      () =>
        sql`
          SELECT *
          FROM smart_cards
          WHERE smart_view_id = $1
          ORDER BY sort_key ASC NULLS LAST,
                   created_at ASC NULLS LAST
        `,
      []
    );

    const isReady = this.connectionManager.isReady();

    useEffect(() => {
      if (!smartViewId) {
        setCards([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      if (!isReady) {
        return;
      }

      const controller = new AbortController();
      const db = this.getDbIfReady();
      if (!db) {
        return () => {
          controller.abort();
        };
      }
      let cleanup: (() => void) | undefined;

      db.live
        .query<SmartCardRow>({ query, params: [smartViewId], signal: controller.signal })
        .then(({ initialResults, subscribe, unsubscribe }) => {
          const apply = (rows: SmartCardRow[]) => {
            setCards(rows);
            setIsLoading(false);
          };

          apply(initialResults.rows as SmartCardRow[]);

          const onUpdate = (results: { rows: SmartCardRow[] }) => apply(results.rows);
          subscribe(onUpdate);
          cleanup = () => void unsubscribe(onUpdate);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          setError(err?.message ?? "Unknown error");
          setIsLoading(false);
        });

      return () => {
        controller.abort();
        if (cleanup) cleanup();
      };
    }, [query, smartViewId, isReady]);

    return { cards, isLoading, error };
  }
}
